import React, {ChangeEvent, useCallback, useEffect, useState} from "react";
import {Modal} from "components";
import {getSupportedCountries, showSupportedCountries} from "apps/DRAW_PREDICTOR/modules/selectors";
import {useDispatch, useSelector} from "react-redux";
import {getUser} from "modules/selectors";
import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
} from "@mui/material";
import {ReactComponent as QatarLogo} from "assets/images/logos/qatarLogo.svg";
import styled from "styled-components";
import {updateUser} from "modules/actions";
import {useTranslation} from "react-i18next";
import {NavLink} from "react-router-dom";
import {LANGUAGE_PATH, trackAdobeAnalytics} from "modules";
import {PrimaryButton, SecondaryButton} from "apps/DRAW_PREDICTOR/components";
import {handleModalState} from "apps/DRAW_PREDICTOR/modules/actions/modals";
import {get} from "lodash";

const ModalInner = styled.div`
	text-align: center;
	padding: 20px;
	h2 {
		font-family: "Qatar2022";
		font-style: normal;
		font-weight: bold;
		font-size: 25px;
		line-height: 120%;
		margin: 30px auto 10px;
		max-width: 460px;
	}
	h3 {
		max-width: 500px;
		font-family: "Graphik";
		font-style: normal;
		font-weight: normal;
		font-size: 18px;
		line-height: 160%;
		margin: 0 auto 20px;
	}
`;

const Buttons = styled.div`
	margin-top: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 20px;
`;
const LabelLink = styled.p`
	font-family: "Poppins";
	font-style: normal;
	font-weight: 500;
	font-size: 14px;
	line-height: 160%;
	a {
		text-decoration: underline;
	}
`;

export const SupportedCountryModal: React.FC = () => {
	const dispatch = useDispatch();
	const countries = useSelector(getSupportedCountries);
	const user = useSelector(getUser);
	const {t} = useTranslation(LANGUAGE_PATH);
	const isNeedToShow = useSelector(showSupportedCountries);
	const [selected, setSelected] = useState("");
	const [terms, setTerms] = useState(false);
	const [disabled, setDisabled] = useState(true);

	const handleChange = (event: SelectChangeEvent<string>) => {
		setSelected(event.target.value);
	};
	const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
		setTerms(checked);
	};
	const handleModalClose = useCallback(() => {
		dispatch(handleModalState({supportedCountries: false}));
	}, [dispatch]);

	useEffect(() => {
		setDisabled(!(terms && selected));
	}, [terms, selected]);

	const handleUpdateUser = () => {
		handleModalClose();
		dispatch(
			updateUser({
				...user,
				supportedCountry: selected || "",
			})
		);
		const country = countries.find((c) => c.key === selected);
		trackAdobeAnalytics({
			section: ":support-country",
			eventName: "drawNationSelect",
			team: get(country, "value", ""),
		});
	};

	const showTour = () => {
		handleUpdateUser();
		dispatch(handleModalState({tour: true}));
	};

	if (!isNeedToShow) {
		return null;
	}
	const label = {inputProps: {"aria-label": "Terms and Conditions"}};

	return (
		<Modal>
			<ModalInner>
				<QatarLogo />
				<h2>{t("modal.welcome.header_copy")}</h2>
				<h3>{t("modal.welcome.body_copy")}</h3>
				<div>
					<FormControl variant="standard" sx={{m: 1, minWidth: 320}}>
						<InputLabel id="supported-country-label">
							{t("modal.welcome.dropdown_header")}
						</InputLabel>
						<Select
							labelId="supported-country-label"
							id="supported-country"
							value={selected}
							onChange={handleChange}
							label={t("modal.welcome.dropdown_header")}>
							<MenuItem value="">
								<em>{t("modal.welcome.dropdown_text")}</em>
							</MenuItem>
							{countries.map((country) => (
								<MenuItem key={country.key} value={country.key}>
									{country.value}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</div>
				<div>
					<FormControl sx={{m: 3}} component="fieldset" variant="standard">
						<FormGroup>
							<FormControlLabel
								control={
									<Checkbox
										onChange={handleCheckboxChange}
										{...label}
										sx={{"& .MuiSvgIcon-root": {fontSize: 34}}}
										name="terms"
									/>
								}
								label={
									<LabelLink>
										<NavLink to="/help/guidelines">
											{t("modal.welcome.terms")}
										</NavLink>
									</LabelLink>
								}
							/>
						</FormGroup>
					</FormControl>
				</div>
				<Buttons>
					<PrimaryButton disabled={disabled} onClick={handleUpdateUser}>
						{t("modal.welcome.button_pick")}
					</PrimaryButton>
					<SecondaryButton disabled={disabled} onClick={showTour}>
						{t("modal.welcome.button_tutorial")}
					</SecondaryButton>
				</Buttons>
			</ModalInner>
		</Modal>
	);
};
